import { useRef, useState } from 'react';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';

import { fetchROC } from '@/adapters/fetchExaCare';
import type { RocDocumentDownloadArgs } from '@/components/roc/DocumentMessageCard';
import { useSnackbar } from '@/hooks/useSnackbar';
import { formatFileSize } from '@/utils/formatters';
import { isPreviewableDocument } from '@/utils/isPreviewableDocument';

import { useDocumentsQuery } from '../../hooks/useDocumentsQuery';

interface DocumentsTabProps {
  referralId: string;
  documents?: DocumentsTabDocument[];
  isLoading?: boolean;
  isError?: boolean;
  onOpenDocument?: (args: RocDocumentDownloadArgs) => Promise<void>;
  actionVariant?: 'download' | 'open';
  suppressAutocapture?: boolean;
}

export interface DocumentsTabDocument {
  document_id: string;
  referral_id: string;
  file_name: string;
  created_at: string;
  page_count: number;
  file_size_bytes: number;
}

interface DocumentRowProps {
  referralId: string;
  documentId: string;
  fileName: string;
  createdAt: string;
  pageCount: number;
  fileSizeBytes: number;
  onOpenDocument?: (args: RocDocumentDownloadArgs) => Promise<void>;
  actionVariant?: 'download' | 'open';
  suppressAutocapture?: boolean;
}

function DocumentRow({
  referralId,
  documentId,
  fileName,
  createdAt,
  pageCount,
  fileSizeBytes,
  onOpenDocument,
  actionVariant = 'download',
  suppressAutocapture = false
}: DocumentRowProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const isOpeningRef = useRef(false);
  const { showSnackbar } = useSnackbar();
  const canPreview =
    actionVariant === 'open' && isPreviewableDocument(fileName);

  const handleOpen = async () => {
    if (isOpeningRef.current) {
      return;
    }
    isOpeningRef.current = true;
    setIsLoading(true);
    try {
      if (onOpenDocument) {
        await onOpenDocument({ referralId, documentId, fileName });
      } else {
        const { url } = await fetchROC.get<{ url: string }>(
          `/referrals/${referralId}/documents/${documentId}/download-url`
        );
        const parsed = new URL(url);
        if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
          throw new Error('Unexpected download URL protocol');
        }
        const opened = window.open(
          parsed.toString(),
          '_blank',
          'noopener,noreferrer'
        );
        if (opened) {
          opened.opener = null;
        }
      }
    } catch {
      showSnackbar({
        message: 'Failed to open document. Please try again.',
        severity: 'error'
      });
    } finally {
      isOpeningRef.current = false;
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: 'info.main', borderRadius: 1.5, px: 2, py: 1.5 }}>
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ fontSize: 13, flex: 1, minWidth: 0 }}
        >
          {fileName}
        </Typography>
        <Stack direction="row" alignItems="center" flexShrink={0}>
          <Tooltip title={canPreview ? 'Preview' : 'Download'}>
            <IconButton
              size="small"
              disabled={isLoading}
              className={suppressAutocapture ? 'ph-no-capture' : undefined}
              onClick={handleOpen}
            >
              {isLoading ? (
                <CircularProgress size={16} />
              ) : canPreview ? (
                <VisibilityOutlinedIcon
                  fontSize="small"
                  sx={{ color: 'text.secondary' }}
                />
              ) : (
                <DownloadIcon
                  fontSize="small"
                  sx={{ color: 'text.secondary' }}
                />
              )}
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={() => setIsExpanded((prev) => !prev)}
            sx={{ color: 'text.secondary' }}
          >
            <KeyboardArrowDownIcon
              fontSize="small"
              sx={{
                transition: 'transform 0.2s',
                transform: isExpanded ? 'rotate(180deg)' : 'none'
              }}
            />
          </IconButton>
        </Stack>
      </Stack>
      <Collapse in={isExpanded}>
        <Stack gap={0.25} mt={0.5}>
          <Typography variant="caption" color="text.secondary">
            Uploaded on {dayjs(createdAt).format('MMM D, YYYY h:mm A')}
          </Typography>
          {pageCount > 0 && (
            <Typography variant="caption" color="text.secondary">
              {pageCount} Pages
            </Typography>
          )}
          {fileSizeBytes > 0 && (
            <Typography variant="caption" color="text.secondary">
              {formatFileSize(fileSizeBytes)}
            </Typography>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
}

export function DocumentsTab({
  referralId,
  documents: providedDocuments,
  isLoading: providedIsLoading,
  isError: providedIsError,
  onOpenDocument,
  actionVariant,
  suppressAutocapture = false
}: DocumentsTabProps) {
  const query = useDocumentsQuery(referralId, {
    enabled: providedDocuments === undefined
  });
  const documents = providedDocuments ?? query.data;
  const isLoading = providedIsLoading ?? query.isLoading;
  const isError = providedIsError ?? query.isError;

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" flex={1} py={6}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (isError) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        flex={1}
        py={6}
        gap={1}
      >
        <DescriptionOutlinedIcon
          sx={{ fontSize: 36, color: 'text.disabled' }}
        />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Failed to load documents. Please try again.
        </Typography>
      </Stack>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        flex={1}
        py={6}
        gap={1}
      >
        <DescriptionOutlinedIcon
          sx={{ fontSize: 36, color: 'text.disabled' }}
        />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No documents attached to this referral.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }} gap={1.5}>
      {documents.map((doc) => (
        <DocumentRow
          key={doc.document_id}
          referralId={referralId}
          documentId={doc.document_id}
          fileName={doc.file_name}
          createdAt={doc.created_at}
          pageCount={doc.page_count}
          fileSizeBytes={doc.file_size_bytes}
          onOpenDocument={onOpenDocument}
          actionVariant={actionVariant}
          suppressAutocapture={suppressAutocapture}
        />
      ))}
    </Stack>
  );
}
